import {
  faArrowDown,
  faArrowUp,
  faCheck,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { camelCase } from "lodash";
import { Goal } from "model/Goals";
import { Trans, useTranslation } from "react-i18next";
import { MilestoneRatio } from "shared/enums/MilestoneRatio";
import { motion } from "motion/react";
import SubscriptionDefaultCTA from "shared/components/subscription/SubscriptionDefaultCTA";
import { useMemo } from "react";
import useSubscription, {
  SubscriptionPlan,
} from "shared/hooks/useSubscription";

interface MetaProps extends Goal {
  footer?: React.ReactNode;
  animationDelay?: number;
}

const Meta: React.FC<MetaProps> = (props) => {
  const {
    milestone,
    name,
    currentValue,
    ratioSelected,
    completed,
    footer,
    animationDelay = 0,
  } = props;
  const { t } = useTranslation();
  const { currentPlan } = useSubscription();

  const percentage = Math.min((currentValue / milestone) * 100, 100);

  const needsUpgrade = useMemo(() => {
    return !name?.includes("spotify") && currentPlan === SubscriptionPlan.START;
  }, [name, currentPlan]);

  const getIconByRatio = () => {
    if (!ratioSelected) return null;
    if (completed) return <FontAwesomeIcon icon={faArrowUp} color="#00D88A" />;
    if (ratioSelected === MilestoneRatio.Ascending)
      return <FontAwesomeIcon icon={faArrowUp} color="#00D88A" />;
    if (ratioSelected === MilestoneRatio.Descending)
      return <FontAwesomeIcon icon={faArrowDown} color="#FF9191" />;
    if (ratioSelected === MilestoneRatio.Stable)
      return <FontAwesomeIcon icon={faMinus} color="#F2CF51" />;
  };

  const getRatioTranslation = () => {
    const translate = (name: string) => t(`pages.Metas.${name}`);

    if (completed) return translate("concluido");
    if (ratioSelected === MilestoneRatio.Ascending)
      return translate("crescendo");
    if (ratioSelected === MilestoneRatio.Descending)
      return translate("decrescendo");
    if (ratioSelected === MilestoneRatio.Stable) return translate("estavel");
  };

  const getColorByRatio = () => {
    if (!ratioSelected) return "";
    if (completed) return "#00D88A";
    if (ratioSelected === MilestoneRatio.Ascending) return "#00D88A";
    if (ratioSelected === MilestoneRatio.Descending) return "#FF4F4F";
    if (ratioSelected === MilestoneRatio.Stable) return "#F2CF51";
  };

  return (
    <motion.div
      className="meta-card"
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: animationDelay, duration: 0.2 }}
    >
      {needsUpgrade ? (
        <div>
          <span
            className="goal-name body-small"
            style={{
              display: "block",
              marginBottom: 14,
            }}
          >
            {t(`pages.Metas.${camelCase(name)}`)}
          </span>
          <SubscriptionDefaultCTA
            title={
              <Trans
                i18nKey={t("components.Subscription.upgradeFeatureCTATitle")}
                components={{ span: <span className="cor-destaque" /> }}
              />
            }
          />
        </div>
      ) : (
        <>
          <div className="container">
            <div>
              <span
                className="goal-name body-small"
                style={{
                  display: "block",
                  marginBottom: 14,
                }}
              >
                {t(`pages.Metas.${camelCase(name)}`)}
              </span>
              {completed && (
                <FontAwesomeIcon
                  icon={faCheck}
                  style={{ color: "#00D88A", marginRight: 4 }}
                />
              )}
              <span className="body-large">
                {`${
                  currentValue ? Math.floor(currentValue).toLocaleString() : "-"
                }`}{" "}
                <span style={{ color: "#fff" }}>{`/${Math.floor(
                  milestone
                ).toLocaleString()}`}</span>
              </span>
            </div>

            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              {ratioSelected && (
                <>
                  <div>{getIconByRatio()}</div>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    {/* <span>{t("pages.Metas.evolucao")}</span> */}
                    <span
                      style={{
                        color: getColorByRatio(),
                        fontSize: "1rem",
                      }}
                    >
                      {getRatioTranslation()}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              marginTop: 16,
            }}
          >
            <div
              style={{
                height: 6,
                width: `${percentage}%`,
                borderRadius: completed ? "3px" : "3px 0px 0px 3px",
              }}
              className={completed ? "progress completed" : "progress"}
            />
            <div
              style={{
                height: 6,
                width: `${100 - percentage}%`,

                borderRadius: "0px 3px 3px 0px",
              }}
              className="trail"
            />
          </div>

          {footer && footer}
        </>
      )}
    </motion.div>
  );
};

export default Meta;
